import type { Node, NodeFrame, NodeId } from '@menuvera/engine';

import { findNodeIn } from '../nodeTree';
import { findOverlayPlacement } from './overlayUtils';

export function resolveNodeFrame(root: Node, nodeId: NodeId): NodeFrame | null {
  const node = findNodeIn(root, nodeId);
  if (!node) return null;
  if (node.frame) return node.frame;

  const placement = findOverlayPlacement(root, nodeId);
  if (!placement) return null;

  return {
    x: placement.child.x,
    y: placement.child.y,
    width: placement.child.width,
    height: placement.child.height,
    rotate: placement.child.rotate,
    z: placement.child.z,
    pinX: placement.child.pinX,
    pinY: placement.child.pinY,
  };
}

export function collectFramedNodeRects(
  root: Node,
): Array<{ id: string; x: number; y: number; width: number; height: number }> {
  const out: Array<{ id: string; x: number; y: number; width: number; height: number }> = [];

  const visit = (node: Node) => {
    if (node.frame) {
      out.push({
        id: node.id,
        x: node.frame.x,
        y: node.frame.y,
        width: node.frame.width,
        height: node.frame.height,
      });
    }
    if (node.kind === 'overlay') {
      for (const child of node.children) {
        if (!child.node.frame) {
          out.push({
            id: child.node.id,
            x: child.x,
            y: child.y,
            width: child.width,
            height: child.height,
          });
        }
        visit(child.node);
      }
      return;
    }
    if (node.kind === 'stack' || node.kind === 'grid' || node.kind === 'group' || node.kind === 'frame') {
      for (const child of node.children) visit(child);
      return;
    }
    if (node.kind === 'repeater') {
      visit(node.itemTemplate);
      if (node.empty) visit(node.empty);
      return;
    }
    if (node.kind === 'slot' && node.defaultChild) visit(node.defaultChild);
  };

  visit(root);
  return out;
}

export function hasSpatialFrame(root: Node, nodeId: NodeId): boolean {
  return resolveNodeFrame(root, nodeId) !== null;
}

export function maxFrameZ(node: Node): number {
  let max = node.frame?.z ?? 0;
  switch (node.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame':
      for (const child of node.children) max = Math.max(max, maxFrameZ(child));
      break;
    case 'overlay':
      for (const child of node.children) {
        max = Math.max(max, child.z ?? 0, maxFrameZ(child.node));
      }
      break;
    case 'repeater':
      max = Math.max(max, maxFrameZ(node.itemTemplate));
      if (node.empty) max = Math.max(max, maxFrameZ(node.empty));
      break;
    case 'slot':
      if (node.defaultChild) max = Math.max(max, maxFrameZ(node.defaultChild));
      break;
    default:
      break;
  }
  return max;
}
